/* eslint-disable react/require-default-props */
import { Popover } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import moment from 'moment';
import { FocusEvent, PureComponent, ReactNode } from 'react';
import { DateType } from 'api/models';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { queueMacrotask } from 'utils/macrotask';
import { uid } from 'utils/uid';
import { DueDateRangePicker } from './range-picker';
import { DueDateSinglePicker } from './single-picker';
import { DueDatePickerTrigger } from './trigger';
import { DateValueType, DueDatePickerButtonProps, RangeType } from './types';

const DATE_FORMAT = 'DD/MM/YYYY';

export const API_DATE_FORMAT = 'YYYY-MM-DD';

export type DateValue = DateType | null;
type DueDatePickerProps = DueDatePickerButtonProps & {
  footerText?: string;
  endDateText?: string;
  disabledDate?(
    date: moment.Moment,
    startDate?: moment.Moment | null,
    endDate?: moment.Moment | null,
  ): boolean;
  endDateTextRange?: string;
  canChooseStartDate?: boolean;
  hideModifyPicker?: boolean;
  hideClearDate?: boolean;
  hideFooter?: boolean;
  isTodayDisabled?: boolean;
  isTomorrowDisabled?: boolean;
  initialVisibile?: boolean;
  isRangePicker?: boolean;
  useAsStartDatePicker?: boolean;
  disallowSingleDate?: boolean;
  placeholder?: string | ReactNode;
  startDate?: DateValue;
  dueDate?: DateValue;
  overlayClassName?: string;
  renderTrigger?: (handler) => string | ReactNode;
  onDateChange?(values: [DateValue, DateValue]): void;
  picker?: 'date' | 'time' | 'week' | 'month' | 'quarter' | 'year';
  hideTrigger?: boolean;
  dateRenderer?(
    currentDate: moment.Moment,
    today: moment.Moment,
  ): React.ReactNode;
  hideIcon?: boolean;
  handleOnFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  placement?: TooltipPlacement;
  panelRender?: (originPanel: React.ReactNode) => React.ReactNode;
};

type DueDatePickerState = {
  datePickerVisibility: boolean;
  isRangePicker: boolean;
  startDate: DateValueType;
  dueDate: DateValueType;
};

/* NOTE:: In the interest of time, copy pasted DueDatePicker component and made some changes to accommodate YYYY-MM-DD.
 * So, CustomDueDatePicker exact replica of DueDatePicker except startDate, dueDate &  onDateChange.
 *  */
export class CustomDueDatePicker extends PureComponent<
  DueDatePickerProps,
  DueDatePickerState
> {
  singlePopoverRefId: string;

  rangePopoverRefId: string;

  popoverRefId = '';

  constructor(props: DueDatePickerProps) {
    super(props);

    this.rangePopoverRefId = uid('due-date-picker-range');
    this.singlePopoverRefId = uid('due-date-picker-single');
    const {
      startDate,
      dueDate,
      initialVisibile,
      isRangePicker: isRangePickerProps,
    } = props;
    const isRangePicker = isRangePickerProps || !!startDate;

    this.state = {
      datePickerVisibility: !!initialVisibile,
      startDate: startDate ? moment(startDate) : null,
      dueDate: dueDate ? moment(dueDate) : null,
      isRangePicker,
    };

    this.updateDate = this.updateDate.bind(this);
    this.updateStartDate = this.updateStartDate.bind(this);
    this.updateDueDate = this.updateDueDate.bind(this);
    this.clearDates = this.clearDates.bind(this);
    this.modifyPickerType = this.modifyPickerType.bind(this);
    this.pickToday = this.pickToday.bind(this);
    this.pickTomorrow = this.pickTomorrow.bind(this);
    this.toggleDatePickerVisibility =
      this.toggleDatePickerVisibility.bind(this);
    this.showDatePicker = this.showDatePicker.bind(this);
    this.closeDatePicker = this.closeDatePicker.bind(this);
    this.popupContainer = this.popupContainer.bind(this);
    this.focusInsideFirstInputField =
      this.focusInsideFirstInputField.bind(this);
    this.setPicker = this.setPicker.bind(this);
    this.setDueDate = this.setDueDate.bind(this);
    this.setStartDate = this.setStartDate.bind(this);
    this.setPicker(isRangePicker);
  }

  componentDidUpdate({
    dueDate: oldDueDate,
    startDate: oldStartDate,
  }: DueDatePickerProps): void {
    const { dueDate: newDueDate, startDate: newStartDate } = this.props;

    if (newDueDate !== oldDueDate) {
      this.setDueDate(newDueDate);
    }
    if (newStartDate !== oldStartDate) {
      this.setStartDate(newStartDate);
    }
  }

  setPicker(isRangePicker: boolean): void {
    this.popoverRefId = isRangePicker
      ? this.rangePopoverRefId
      : this.singlePopoverRefId;

    this.setState({
      isRangePicker,
    });
  }

  setDueDate(newDueDate?: DateValue): void {
    this.setState({
      dueDate: newDueDate ? moment(newDueDate) : null,
    });
  }

  setStartDate(newStartDate?: DateValue): void {
    this.setState({
      startDate: newStartDate ? moment(newStartDate) : null,
    });
  }

  focusInsideFirstInputField(): void {
    queueMacrotask(() => {
      const $input = document.querySelector(
        `#${this.popoverRefId} .ant-picker-input input`,
      ) as HTMLInputElement;

      if ($input) {
        $input.focus();
      }
    });
  }

  updateDate(values: RangeType): void {
    const [updatedStartDate, updatedDueDate] = values ?? [null, null];

    this.setState({
      startDate: updatedStartDate,
      dueDate: updatedDueDate,
    });
  }

  updateDueDate(date: DateValueType): void {
    this.updateDate([null, date]);
    /* NOTE:: we need to queue the following toggleDatePickerVisibility, as the above updateDate is gonna call setState which is a async. */
    queueMacrotask(() => this.toggleDatePickerVisibility(false));
  }

  updateStartDate(date: DateValueType): void {
    this.updateDate([date, null]);
    queueMacrotask(() => this.toggleDatePickerVisibility(false));
  }

  clearDates(): void {
    this.updateDate([null, null]);
    /* NOTE:: we need to queue the following toggleDatePickerVisibility, as the above updateDate is gonna call setState which is a async. */
    queueMacrotask(() => this.toggleDatePickerVisibility(false));
  }

  modifyPickerType(): void {
    const { isRangePicker } = this.state;

    this.setPicker(!isRangePicker);

    this.focusInsideFirstInputField();
  }

  pickToday(): void {
    // do not switch from range picker to single picker if hideModifyPicker is true
    // this will apply in places like timeoff picker where we don't want to allow user to switch from range picker to single picker
    const { hideModifyPicker } = this.props;
    const { useAsStartDatePicker = false } = this.props;

    this.setState({
      isRangePicker: !!hideModifyPicker,
    });

    if (useAsStartDatePicker) {
      return this.updateStartDate(moment().startOf('day'));
    }

    return this.updateDueDate(moment().startOf('day'));
  }

  pickTomorrow(): void {
    // do not switch from range picker to single picker if hideModifyPicker is true
    // this will apply in places like timeoff picker where we don't want to allow user to switch from range picker to single picker
    const { hideModifyPicker } = this.props;
    const { useAsStartDatePicker = false } = this.props;

    this.setState({
      isRangePicker: !!hideModifyPicker,
    });

    if (useAsStartDatePicker) {
      return this.updateStartDate(
        moment().clone().add(1, 'day').startOf('day'),
      );
    }

    return this.updateDueDate(moment().clone().add(1, 'day').startOf('day'));
  }

  toggleDatePickerVisibility(visibility: boolean): void {
    const {
      props: { onDateChange },
      state,
    } = this;

    // call onDateChange when the popover closes.
    if (!visibility && onDateChange) {
      onDateChange([
        state.startDate?.format(API_DATE_FORMAT) ?? null,
        state.dueDate?.format(API_DATE_FORMAT) ?? null,
      ]);
    }
    this.setState({
      datePickerVisibility: visibility,
    });
  }

  showDatePicker(): void {
    this.toggleDatePickerVisibility(true);

    this.focusInsideFirstInputField();
  }

  closeDatePicker(): void {
    this.toggleDatePickerVisibility(false);
  }

  popupContainer(): HTMLElement {
    return document.getElementById(this.popoverRefId)!;
  }

  render(): ReactNode {
    const { datePickerVisibility, isRangePicker, startDate, dueDate } =
      this.state;

    const {
      canChooseStartDate = true,
      useAsStartDatePicker = false,
      hideFooter,
      isTodayDisabled: isTodayDisabledProp,
      isTomorrowDisabled: isTomorrowDisabledProp,
      footerText,
      endDateText,
      endDateTextRange,
      disallowSingleDate,
      hideModifyPicker,
      hideClearDate,
      className,
      renderTrigger,
      block,
      disabled,
      id,
      placeholder,
      overlayClassName,
      size = 'middle',
      disabledDate,
      picker,
      hideTrigger,
      dateRenderer,
      hideIcon = false,
      handleOnFocus,
      placement,
      panelRender,
    } = this.props;

    const {
      clearDates,
      pickToday,
      pickTomorrow,
      modifyPickerType,
      showDatePicker,
      toggleDatePickerVisibility,
      updateDate,
      updateStartDate,
      updateDueDate,
      popupContainer,
      closeDatePicker,
    } = this;

    const hasValue = !!(startDate || dueDate);
    const dateValue = useAsStartDatePicker ? startDate : dueDate;
    const updateDates = useAsStartDatePicker ? updateStartDate : updateDueDate;

    // Calculate if today/tomorrow are disabled if not explicitly provided
    const today = moment().startOf('day');
    const tomorrow = moment().add(1, 'day').startOf('day');
    const isTodayDisabled =
      isTodayDisabledProp ?? (disabledDate ? disabledDate(today) : false);
    const isTomorrowDisabled =
      isTomorrowDisabledProp ?? (disabledDate ? disabledDate(tomorrow) : false);

    const displayTrigger = () => {
      if (hideTrigger) {
        return null;
      }
      if (renderTrigger) {
        return renderTrigger(showDatePicker);
      }

      return (
        <DueDatePickerTrigger
          className={className}
          block={block}
          disabled={disabled}
          hasValue={hasValue}
          id={id}
          placeholder={placeholder}
          size={size}
          startDate={startDate}
          dueDate={dueDate}
          onClick={() => {
            setMarkerForDataDog(ClickEventTags.DueDatePickerPopup);
            showDatePicker();
          }}
          hideIcon={hideIcon}
        />
      );
    };

    return (
      <Popover
        id={this.popoverRefId}
        content={
          isRangePicker ? (
            <DueDateRangePicker
              disallowSingleDate={disallowSingleDate}
              hideModifyPicker={hideModifyPicker}
              hideClearDate={hideClearDate}
              canChooseStartDate={canChooseStartDate}
              getPopupContainer={popupContainer}
              format={DATE_FORMAT}
              value={[startDate, dueDate]}
              updateDateRange={updateDate}
              clearDates={clearDates}
              pickToday={pickToday}
              hideFooter={hideFooter}
              endDateTextRange={endDateTextRange}
              footerText={footerText}
              pickTomorrow={pickTomorrow}
              modifyPickerType={modifyPickerType}
              onDone={closeDatePicker}
              onFocus={handleOnFocus}
              disabledDate={(curr) =>
                disabledDate?.(curr, startDate, dueDate) ?? false
              }
              dateRenderer={dateRenderer}
              isTodayDisabled={isTodayDisabled}
              isTomorrowDisabled={isTomorrowDisabled}
              panelRender={panelRender ?? undefined}
            />
          ) : (
            <DueDateSinglePicker
              endDateText={endDateText}
              footerText={footerText}
              canChooseStartDate={canChooseStartDate}
              useAsStartDatePicker={useAsStartDatePicker}
              hideClearDate={hideClearDate}
              getPopupContainer={popupContainer}
              value={dateValue}
              format={DATE_FORMAT}
              updateDates={updateDates}
              clearDates={clearDates}
              pickToday={pickToday}
              hideFooter={hideFooter}
              pickTomorrow={pickTomorrow}
              modifyPickerType={modifyPickerType}
              onDone={closeDatePicker}
              disabledDate={disabledDate}
              dateRenderer={dateRenderer}
              picker={picker}
              isTodayDisabled={isTodayDisabled}
              isTomorrowDisabled={isTomorrowDisabled}
            />
          )
        }
        placement={placement ?? 'bottomLeft'}
        trigger="click"
        open={datePickerVisibility}
        onOpenChange={toggleDatePickerVisibility}
        overlayClassName={`due__date__picker ${overlayClassName || ''}`}
        destroyTooltipOnHide
      >
        {displayTrigger()}
      </Popover>
    );
  }
}
